import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";

/* ================= PAGES ================= */

import Home from "./pages/Home";
import Login from "./pages/Login";
import Signup from "./pages/Signup/Signup";
import SelectPersona from "./pages/Signup/SelectPersona";
import CompleteProfile from "./pages/CompleteProfile/CompleteProfile";
import HowPrufyyWorks from "./pages/HowPrufyyWorks";
import LogoutScreen from "./pages/LogoutScreen";

/* ===== SOLUTIONS PAGES ===== */
import Founder from "./pages/Solutions/Founder";
import Incubator from "./pages/Solutions/Incubator";
import Accelerator from "./pages/Solutions/Accelerator";
import Angel from "./pages/Solutions/Angel";
import VC from "./pages/Solutions/VC";

/* ================= LEGAL PAGES ================= */
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import Contact from "./pages/Contact";

/* ================= ROUTE GUARDS ================= */

import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";

/* ================= FOUNDER DASHBOARD ================= */

import DashboardLayout from "./pages/Dashboard/DashboardLayout";
import Profile from "./pages/Dashboard/Profile";
import Pitch from "./pages/Dashboard/Pitch";
import Claims from "./pages/Dashboard/Claims";
import StartupRecords from "./pages/Dashboard/StartupRecords";
import Analytics from "./pages/Dashboard/Analytics";
import CredibilityFeedback from "./pages/Dashboard/CredibilityFeedback";
import ProgressFeed from "./pages/Dashboard/ProgressFeed";

/* ================= EVALUATOR ================= */

import EvaluatorOnboarding from "./pages/EvaluatorOnboarding/EvaluatorOnboarding";
import EvaluatorDashboardLayout from "./pages/EvaluatorDashboard/EvaluatorDashboardLayout";
import EvaluatorProfile from "./pages/EvaluatorDashboard/Profile";
import SharedWithMe from "./pages/EvaluatorDashboard/SharedWithMe";
import MyReviews from "./pages/EvaluatorDashboard/MyReviews";
import StartupView from "./pages/Evaluator/StartupView";
import Cohorts from "./pages/EvaluatorDashboard/Cohorts";
import CohortDetail from "./pages/EvaluatorDashboard/CohortDetail";
import EvaluatorAnalytics from "./pages/EvaluatorDashboard/Analytics";
import EvaluatorProgressFeed from "./pages/EvaluatorDashboard/ProgressFeed";

/* ================= OTHER ================= */

import PublicProfile from "./pages/PublicProfile/PublicProfile";
import ReviewDashboard from "./pages/Internal/ReviewDashboard";
import EvidenceRedirect from "./pages/EvidenceRedirect";
import StartupRecordRedirect from "./pages/StartupRecordRedirect";

function App() {
  const { user, profile, loading } = useAuth();

  if (loading) return null;

  return (
    <BrowserRouter>
      <div className="app-root">
        <Routes>
          {/* ================= HOME ================= */}
          <Route path="/" element={<Home />} />
          <Route path="/how-prufyy-works" element={<HowPrufyyWorks />} />
          <Route path="/logout" element={<LogoutScreen />} />

          {/* ================= SOLUTIONS ================= */}
          <Route path="/solutions/founder" element={<Founder />} />
          <Route path="/solutions/incubator" element={<Incubator />} />
          <Route path="/solutions/accelerator" element={<Accelerator />} />
          <Route path="/solutions/angel" element={<Angel />} />
          <Route path="/solutions/vc" element={<VC />} />

          {/* ================= LEGAL ================= */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/contact" element={<Contact />} />

          {/* ================= AUTH ================= */}
          <Route
            path="/login"
            element={
              user ? (
                profile ? (
                  profile.persona === "evaluator" ? (
                    <Navigate to="/evaluator/shared" replace />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                ) : null
              ) : (
                <Login />
              )
            }
          />

          <Route
            path="/signup"
            element={
              user ? <Navigate to="/select-persona" replace /> : <Signup />
            }
          />

          {/* ================= PERSONA ================= */}
          <Route
            path="/select-persona"
            element={
              <ProtectedRoute>
                <SelectPersona />
              </ProtectedRoute>
            }
          />

          {/* ================= ONBOARDING ================= */}
          <Route
            path="/complete-profile"
            element={
              <ProtectedRoute>
                <CompleteProfile />
              </ProtectedRoute>
            }
          />

          <Route
            path="/evaluator-onboarding"
            element={
              <ProtectedRoute>
                <EvaluatorOnboarding />
              </ProtectedRoute>
            }
          />

          {/* ================= FOUNDER DASHBOARD ================= */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Profile />} />
            <Route path="profile" element={<Profile />} />
            <Route path="pitch" element={<Pitch />} />
            <Route path="claims" element={<Claims />} />
            <Route path="startup-records" element={<StartupRecords />} />
            <Route path="analytics" element={<Analytics />} />
            <Route
              path="credibility-feedback"
              element={<CredibilityFeedback />}
            />
            <Route path="progress-feed" element={<ProgressFeed />} />
          </Route>

          {/* ================= EVALUATOR DASHBOARD ================= */}
          <Route
            path="/evaluator"
            element={
              <ProtectedRoute>
                <EvaluatorDashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<SharedWithMe />} />
            <Route path="profile" element={<EvaluatorProfile />} />
            <Route path="shared" element={<SharedWithMe />} />
            <Route path="reviews" element={<MyReviews />} />
            <Route path="analytics" element={<EvaluatorAnalytics />} />
            <Route path="cohorts" element={<Cohorts />} />
            <Route path="cohorts/:cohortId" element={<CohortDetail />} />
            <Route path="startup/:founderId" element={<StartupView />} />
            <Route
              path="progress-feed"
              element={<EvaluatorProgressFeed />}
            />
          </Route>

          {/* ================= PUBLIC ================= */}
          <Route path="/p/share/:token" element={<PublicProfile />} />
          <Route path="/p/:userId" element={<PublicProfile />} />
          <Route path="/evidence/:evidenceId" element={<EvidenceRedirect />} />
          <Route path="/record/:recordId" element={<StartupRecordRedirect />} />

          {/* ================= ADMIN ================= */}
          <Route
            path="/internal/reviews"
            element={
              <AdminRoute>
                <ReviewDashboard />
              </AdminRoute>
            }
          />

          {/* ================= FALLBACK ================= */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
